import { queryOptions, useQuery } from "@tanstack/react-query";
import type { WorkspaceMembership } from "@goalhorns/shared";
import { apiRequest } from "../../lib/api";
import type { Usage, Workspace } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";

export const workspaceQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId] as const,
    queryFn: () => apiRequest<Workspace>(paths.workspace(workspaceId)),
    staleTime: 60_000,
  });

/** The signed-in operator's live workspaces (the server's memberships, not this browser's memory). */
export const workspaceMembershipsQuery = queryOptions({
  queryKey: ["workspace-memberships"] as const,
  queryFn: () => apiRequest<WorkspaceMembership[]>(paths.workspaces),
  staleTime: 30_000,
  retry: false,
});

export function useWorkspaceMemberships() {
  return useQuery(workspaceMembershipsQuery);
}

export const usageQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["usage", workspaceId] as const,
    queryFn: () => apiRequest<Usage>(paths.usage(workspaceId)),
  });

export function useWorkspace(workspaceId: string) {
  return useQuery(workspaceQuery(workspaceId));
}

/** Owners and admins acting as the workspace may manage members. */
export function canManageMembers(role: Workspace["role"] | undefined): boolean {
  return role === "owner" || role === "admin";
}
